import {
	Vue, Component, Watch, Prop, Ref,
} from 'vue-property-decorator';
import * as DB from 'interfaces/database';
import mitt from 'mitt';
import { httpClient } from 'utils/http';
import { TabEvent } from 'interfaces/app';
import { TabulatorFull as Tabulator, CellComponentExtended, ColumnDefinitionExtended } from 'tabulator-tables';
import TabulatorSwitchBox from 'components/Tabulator/TabulatorSwitchBox';
import { createInstance } from 'utils/vue';
import Template from './template.vue';

const eventBus = mitt<TabEvent<DB.ProductCategoryModel>>();
@Component({
	components: {
		TabulatorSwitchBox,
	},
})
export default class BadgeProductCategoryTable extends Vue.extend(Template) {
	@Ref('badgeProductCategories')
	private readonly tableReference!: HTMLDivElement;

	private pageOptions = [5, 10, 15, 20, 25, 30, 35, 40, 45, 50];

	private isLoading = false;

	private checkProductCategory: Record<string, DB.BadgeProductCategoryModel> = {};

	private columnDefs: ColumnDefinitionExtended[] = [];

	private table?: Tabulator;

	private perPage = 20;

	@Prop()
	private readonly routeId!: string;

	private get loggedIn(): boolean {
		return this.$auth.isAuthenticated;
	}

	protected beforeMount(): void {
		this.columnDefs = [
			{ field: 'id', title: 'ID' },
			{
				title: 'Name',
				field: 'name',
				headerFilter: true,
			},
			{
				title: 'Available',
				field: 'url',
				titleFormatter: () => {
					const instance = createInstance({
						component: TabulatorSwitchBox,
						props: {
							checked: false,
							text: 'Available',
							eventName: 'headerSwitchBoxChanged',
							eventBus,
						},
					});

					instance.$mount();
					return (instance.$el as HTMLElement);
				},
				formatter: (cell: CellComponentExtended<DB.ProductCategoryModel>) => {
					const data = cell.getData();
					const instance = createInstance({
						component: TabulatorSwitchBox,
						props: {
							checked: Boolean(data && this.checkProductCategory[data.id]),
							data,
							eventBus,
						},
					});

					instance.$mount();
					return (instance.$el as HTMLElement);
				},
			},
		];
	}

	protected mounted(): void {
		this.getBadgeProductCategory();
		eventBus.on(
			'switchBoxChanged',
			this.switchBoxChanged,
		);
		eventBus.on(
			'headerSwitchBoxChanged',
			this.headerSwitchBoxChanged,
		);
		this.tableInitialization();
		this.table?.on(
			'tableBuilt',
			this.onTableBuilt,
		);
	}

	private tableInitialization(): void {
		this.table = new Tabulator(
			this.tableReference,
			{
				height: '60vh',
				layout: 'fitColumns',
				columns: this.columnDefs,
			},
		);
	}

	private onTableBuilt(): void {
		this.getData();
	}

	private async getData(): Promise<void> {
		const parameter = new URLSearchParams({
			limit: '0',
		});
		this.table?.alert('Loading');
		try {
			await this.getBadgeProductCategory();
			const { data } = await httpClient.get<DB.ProductCategoryModel[]>(`/api/productcategory?${parameter}`);
			// set table data
			this.table?.setData(data);
			const allChecked = this.table?.getData().every((item) => this.checkProductCategory[item.id]);
			if (allChecked) {
				this.updateHeaderBox(true);
			} else {
				this.updateHeaderBox(false);
			}
		} catch (error: any) {
			this.$bvToast.toast(
				`${error.message}`,
				{
					solid: true,
					variant: 'danger',
				},
			);
		} finally {
			this.table?.clearAlert();
		}
	}

	private switchBoxChanged(data: TabEvent<DB.ProductCategoryModel>['switchBoxChanged']): void {
		this.table?.alert('Loading');
		if (data.event) {
			httpClient
				.post(
					'/api/badgeproductcategory',
					{
						productcategoryid: data.params.id,
						badgeid: parseInt(
							this.routeId,
							10,
						),
					},
				)
				.then((response) => {
					this.checkProductCategory[response.data.productcategoryid] = response.data;
					// this.table?.redraw();
					const allChecked = this.table?.getData().every((item) => this.checkProductCategory[item.id]);
					this.updateHeaderBox(allChecked as boolean);
					this.table?.scrollToRow(response.data.productcategoryid);
					return undefined;
				})
				.finally(() => {
					this.table?.clearAlert();
				}).catch((err) => {
					this.$bvToast.toast(
						`${err.message}`,
						{
							solid: true,
							variant: 'danger',
						},
					);
				});
		} else {
			httpClient
				.delete(`/api/badgeproductcategory/${this.checkProductCategory[data.params.id].id}`)
				.then(() => {
					delete this.checkProductCategory[data.params.id];
					// this.table?.redraw();
					const allChecked = this.table?.getData().every((item) => this.checkProductCategory[item.id]);
					this.updateHeaderBox(allChecked as boolean);
					this.table?.scrollToRow(data.params.id);
					return undefined;
				})
				.finally(() => {
					this.table?.clearAlert();
				}).catch((err) => {
					this.$bvToast.toast(
						`${err.message}`,
						{
							solid: true,
							variant: 'danger',
						},
					);
				});
		}
	}

	protected beforeDestroy() {
		eventBus.off(
			'switchBoxChanged',
			this.switchBoxChanged,
		);
		eventBus.off(
			'headerSwitchBoxChanged',
			this.headerSwitchBoxChanged,
		);
		this.table?.off(
			'tableBuilt',
			this.onTableBuilt,
		);
		this.table?.destroy();
	}

	private headerSwitchBoxChanged(data: TabEvent<DB.ProductCategoryModel>['headerSwitchBoxChanged']): void {
		this.isLoading = true;
		const allProductCategoryId: number[] = [];

		// Get all the rows from the table
		const allRows = this.table?.getData() || [];

		// Get the current filters applied to the table
		const currentFilters = this.table?.getFilters(true);

		// Manually filter rows based on current filter criteria
		const filteredRows = allRows.filter((row) => currentFilters?.every((filter) => {
			const rowValue = String(row[filter.field]).toLowerCase();
			const filterValue = String(filter.value).toLowerCase();

			return rowValue.includes(filterValue);
		}));

		filteredRows?.map((node) => {
			if (node) {
				allProductCategoryId.push(node.id);
			}
			return undefined;
		});

		const productCategoryId = Object.values(this.checkProductCategory)
			.filter((item) => allProductCategoryId.includes(item.productcategoryid))
			.map((item) => item.id);

		const parameter = new URLSearchParams({
			where: JSON.stringify({
				id: productCategoryId,
			}),
		});

		// Create postData array, but only include offering IDs that are not checked
		const postData = allProductCategoryId
			.filter((productcategory) => !Object.keys(this.checkProductCategory).includes(productcategory.toString()))
			.map((productcategory) => ({
				productcategoryid: productcategory,
				badgeid: parseInt(
					this.routeId,
					10,
				),
			}));

		const url = data.event ? '/api/badgeproductcategory' : `/api/badgeproductcategory?${parameter}`;
		const method = data.event ? 'post' : 'delete';

		httpClient[method](
			url,
			data.event ? postData : undefined,
		)
			.then((response) => {
				if (data.event) {
					response.data.forEach((item: DB.BadgeProductCategoryModel) => {
						this.checkProductCategory[item.productcategoryid] = item;
						this.table?.redraw(true);
					});
				} else {
					allProductCategoryId.forEach((id) => {
						delete this.checkProductCategory[id];
						this.table?.redraw(true);
					});
				}
				return undefined;
			})
			.finally(() => {
				this.isLoading = false;
			})
			.catch((err) => {
				this.$bvToast.toast(
					`${err.message}`,
					{
						solid: true,
						variant: 'danger',
					},
				);
			});
	}

	// This function updates the header formatter switchbox anytime the table renders or redrawn
	private updateHeaderBox(updateCellRenderer: boolean): void {
		const columnDef = this.table?.getColumnDefinitions().map((column) => {
			if (column.field === 'url') {
				return {
					...column,
					title: 'Available',
					field: 'url',
					titleFormatter: updateCellRenderer ? () => {
						const allChecked = this.table?.getData().every((item) => this.table?.getData().length !== 0 && this.checkProductCategory[item.id]);
						const instance = createInstance({
							component: TabulatorSwitchBox,
							props: {
								checked: Boolean(allChecked && this.table?.getData().length !== 0),
								text: 'Available',
								eventName: 'headerSwitchBoxChanged',
								eventBus,
							},
						});

						instance.$mount();
						return (instance.$el as HTMLElement);
					} : column.titleFormatter,
					formatter: (cell: CellComponentExtended<DB.PDPModel>) => {
						const data = cell.getData();
						const instance = createInstance({
							component: TabulatorSwitchBox,
							props: {
								checked: Boolean(data && this.checkProductCategory[data.id]),
								data,
								eventBus,
							},
						});

						instance.$mount();
						return (instance.$el as HTMLElement);
					},
				};
			}
			return column;
		}) as ColumnDefinitionExtended[];

		// update the column definitions
		this.table?.setColumns(columnDef);
	}

	@Watch('loggedIn')
	async getBadgeProductCategory(): Promise<void> {
		if (this.loggedIn) {
			const params = new URLSearchParams({
				where: JSON.stringify({ badgeid: this.routeId }),
				limit: '0',
			});

			try {
				const resp = await httpClient.get<DB.BadgeProductCategoryModel[]>(`/api/badgeproductcategory?${params}`);
				this.checkProductCategory = resp.data.reduce(
					(obj, item) => ({
						...obj,
						[item.productcategoryid]: item,
					}),
					{},
				);
			} catch (error: any) {
				this.$bvToast.toast(
					`${error.message}`,
					{
						solid: true,
						variant: 'danger',
					},
				);
			}
		}
		return undefined;
	}
}
